import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import ConnexionQontoCarte from './ConnexionQontoCarte';
import { AgenceSkipcarPlus, ProfilTaux, messageErreur, skipcarPlusActions, useSkipcarPlusProfils } from './skipcarPlusApi';
import { dateFr, pourcent, tauxDepuisSaisie } from './skipcarPlusFormat';
import { lireRetour } from './skipcarPlusRetour';

import './skipcarPlusTokens.css';
import './skipcarPlusLayout.css';

const PALIERS = ['Sem. 1 à 4', 'Sem. 5 à 8', 'Sem. 9 à 12', 'Sem. 13 à 52'];

const enSaisie = (grille: number[]): string[] => grille.map((taux) => (taux * 100).toString().replace('.', ','));

/** Ligne d'un profil : ses quatre taux, modifiables. Les dossiers existants gardent les leurs. */
const LigneProfil: React.FC<{ profil: ProfilTaux; executer: (action: () => Promise<unknown>) => Promise<void> }> = ({
  profil,
  executer,
}) => {
  const [saisies, setSaisies] = useState(enSaisie(profil.grille));
  const grille = saisies.map(tauxDepuisSaisie);
  const modifie = saisies.join('|') !== enSaisie(profil.grille).join('|');
  const valide = grille.every((taux) => taux !== null);
  const coutMaximum = valide ? (grille as number[]).reduce((somme, taux, index) => somme + taux * (index === 3 ? 40 : 4), 0) : null;

  return (
    <tr>
      <td>
        <strong>{profil.nom}</strong> {profil.systeme && <span className="sk-badge sk-badge-neutral">système</span>}
      </td>
      {saisies.map((saisie, index) => (
        <td key={PALIERS[index]}>
          <input
            className="sk-input"
            value={saisie}
            aria-label={`${profil.nom} ${PALIERS[index]}`}
            onChange={(e) => setSaisies(saisies.map((valeur, i) => (i === index ? e.target.value : valeur)))}
          />
        </td>
      ))}
      <td className="sk-amount">{coutMaximum === null ? 'n.c.' : pourcent(coutMaximum)}</td>
      <td>
        <button
          type="button"
          className="sk-btn sk-btn-secondary"
          disabled={!modifie || !valide}
          onClick={() => executer(() => skipcarPlusActions.modifierProfil(profil.id, grille as number[]))}
        >
          Enregistrer
        </button>
      </td>
    </tr>
  );
};

/** Ligne d'une agence : son profil de taux et son mandat de prélèvement (un seul pour tous ses dossiers). */
const LigneAgence: React.FC<{
  agence: AgenceSkipcarPlus;
  profils: ProfilTaux[];
  executer: (action: () => Promise<unknown>) => Promise<void>;
}> = ({ agence, profils, executer }) => {
  const [dateMandat, setDateMandat] = useState(agence.mandatSigneLe ?? '');
  return (
    <tr>
      <td>
        {agence.nom} <span className="sp-muted">· {agence.nombreDossiers} dossier(s)</span>
      </td>
      <td>
        <select
          className="sk-input"
          value={agence.profilTauxId ?? ''}
          onChange={(e) => executer(() => skipcarPlusActions.profilAgence(agence.orgId, Number(e.target.value)))}
        >
          {agence.profilTauxId === null && <option value="">Standard (par défaut)</option>}
          {profils.map((profil) => (
            <option key={profil.id} value={profil.id}>
              {profil.nom}
            </option>
          ))}
        </select>
      </td>
      <td>
        <div className="sp-row">
          <input className="sk-input" type="date" value={dateMandat} onChange={(e) => setDateMandat(e.target.value)} />
          <button
            type="button"
            className="sk-btn sk-btn-ghost"
            disabled={dateMandat === (agence.mandatSigneLe ?? '')}
            onClick={() => executer(() => skipcarPlusActions.mandatAgence(agence.orgId, dateMandat || null))}
          >
            {dateMandat ? 'Mandat signé' : 'Retirer le mandat'}
          </button>
        </div>
        {agence.mandatSigneLe && <span className="sp-muted">signé le {dateFr(agence.mandatSigneLe)}</span>}
      </td>
    </tr>
  );
};

/** Vue Dieu : profils de taux (lot 1) et réglages Skipcar+ des agences. */
const SkipcarPlusProfilsPage: React.FC = () => {
  const { data, isLoading, error, refetch } = useSkipcarPlusProfils();
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const [nouveauNom, setNouveauNom] = useState('');
  const [nouvelleGrille, setNouvelleGrille] = useState(['1,5', '1', '0,75', '0,5']);
  const [recherche, setRecherche] = useState('');
  const [sansMandatSeulement, setSansMandatSeulement] = useState(false);
  // Ouvert depuis une fiche : le retour y ramène, sinon à la liste Skipcar+.
  const retour = lireRetour(useLocation().state) ?? { vers: '/skipcar-plus', libelle: 'Skipcar+' };

  const executer = async (action: () => Promise<unknown>): Promise<void> => {
    setErreurAction(null);
    try {
      await action();
      await refetch();
    } catch (actionError) {
      setErreurAction(messageErreur(actionError));
    }
  };

  if (isLoading) return <div className="sk-plus sp-page sp-muted">Chargement…</div>;
  if (error || !data) return <div className="sk-plus sp-page sp-error">{messageErreur(error)}</div>;

  const grilleNouvelle = nouvelleGrille.map(tauxDepuisSaisie);
  const creationPossible = nouveauNom.trim() !== '' && grilleNouvelle.every((taux) => taux !== null);
  const termes = recherche.trim().toLocaleLowerCase('fr-FR');
  const sansMandat = data.agences.filter((agence) => agence.mandatSigneLe === null).length;
  const agencesAffichees = data.agences.filter(
    (agence) =>
      (!sansMandatSeulement || agence.mandatSigneLe === null) &&
      (termes === '' || agence.nom.toLocaleLowerCase('fr-FR').includes(termes)),
  );

  return (
    <div className="sk-plus sp-page">
      <div className="sp-head">
        <div>
          <Link className="sp-back" to={retour.vers}>
            ‹ {retour.libelle}
          </Link>
          <h1 className="sk-display">Profils de taux</h1>
          <div className="sp-sub">
            Paliers de 4 / 4 / 4 / 40 semaines, taux hebdomadaires sur la couverture. Un changement ne touche pas les
            dossiers déjà créés.
          </div>
        </div>
      </div>

      <div className="sp-stack">
        <ConnexionQontoCarte />
        <section className="sk-card">
          <div className="sk-card-body sp-table-wrap">
            <table className="sk-table">
              <thead>
                <tr>
                  <th>Profil</th>
                  {PALIERS.map((palier) => (
                    <th key={palier}>{palier} (%)</th>
                  ))}
                  <th className="sk-amount">Coût max. 52 sem.</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.profils.map((profil) => (
                  <LigneProfil key={`${profil.id}-${profil.grille.join('-')}`} profil={profil} executer={executer} />
                ))}
              </tbody>
            </table>
            <div className="sp-inline-form">
              <label>
                <span className="sk-label">Nouveau profil</span>
                <input className="sk-input" value={nouveauNom} onChange={(e) => setNouveauNom(e.target.value)} />
              </label>
              {nouvelleGrille.map((saisie, index) => (
                <label key={PALIERS[index]}>
                  <span className="sk-label">{PALIERS[index]} (%)</span>
                  <input
                    className="sk-input"
                    value={saisie}
                    onChange={(e) =>
                      setNouvelleGrille(nouvelleGrille.map((valeur, i) => (i === index ? e.target.value : valeur)))
                    }
                  />
                </label>
              ))}
              <button
                type="button"
                className="sk-btn sk-btn-primary"
                disabled={!creationPossible}
                onClick={async () => {
                  await executer(() => skipcarPlusActions.creerProfil(nouveauNom.trim(), grilleNouvelle as number[]));
                  setNouveauNom('');
                }}
              >
                Créer le profil
              </button>
            </div>
          </div>
        </section>

        <section className="sk-card">
          <header className="sk-card-header">
            <span className="sk-kicker">Agences Skipcar+</span>
            <span className="sp-muted">profil hérité par chaque nouveau dossier · mandat valable pour tous ses dossiers</span>
          </header>
          <div className="sk-card-body sp-table-wrap">
            <div className="sp-inline-form">
              <label>
                <span className="sk-label">Rechercher une agence</span>
                <input
                  className="sk-input"
                  value={recherche}
                  placeholder="Weecars, Ewigo…"
                  onChange={(e) => setRecherche(e.target.value)}
                />
              </label>
              <label className="sp-row">
                <input type="checkbox" checked={sansMandatSeulement} onChange={(e) => setSansMandatSeulement(e.target.checked)} />
                <span>Sans mandat seulement ({sansMandat} agence{sansMandat > 1 ? 's' : ''} ne peu{sansMandat > 1 ? 'vent' : 't'} pas démarrer de dossier)</span>
              </label>
            </div>
            <table className="sk-table">
              <thead>
                <tr>
                  <th>Agence</th>
                  <th>Profil de taux</th>
                  <th>Mandat de prélèvement</th>
                </tr>
              </thead>
              <tbody>
                {agencesAffichees.map((agence) => (
                  <LigneAgence
                    key={`${agence.orgId}-${agence.profilTauxId}-${agence.mandatSigneLe}`}
                    agence={agence}
                    profils={data.profils}
                    executer={executer}
                  />
                ))}
              </tbody>
            </table>
            {agencesAffichees.length === 0 && <p className="sp-muted">Aucune agence ne correspond.</p>}
          </div>
        </section>
        {erreurAction && <p className="sp-error">{erreurAction}</p>}
      </div>
    </div>
  );
};

export default SkipcarPlusProfilsPage;
